<template>
  <div class="early-renewal-banner">
    <div class="early-renewal-banner__container">
      <div class="early-renewal-banner__crosses-first">
        <Crosses1 />
      </div>
      <div class="early-renewal-banner__content">
        <div class="early-renewal-banner__inner-content">
          <div class="early-renewal-banner__text">
            <div class="early-renewal-banner__header">
              <div class="early-renewal-banner__title">Renew Early & Save!</div>
              <div class="early-renewal-banner__subtitle">
                Save on your {{ currentPlanName[currentPlanCode] }} subscription
                when you renew now.
              </div>
            </div>
            <div class="early-renewal-banner__prices-wrapper">
              <div class="early-renewal-banner__prices">
                <div class="early-renewal-banner__prices-crossed">
                  <div class="early-renewal-banner__prices-crossed-svg">
                    <redCrosses />
                  </div>
                  {{ prices[currentPlanCode].crossed }}
                </div>
                <div class="early-renewal-banner__prices--valid">
                  {{ prices[currentPlanCode].valid }}
                </div>
              </div>
              <div class="early-renewal-banner__small-print">
                Renew now to extend your subscription to {{ newExpirationDate }}
              </div>
            </div>
          </div>
          <div class="early-renewal-banner__cta">
            <button class="btn btn--primary" @click="showModal = true">
              Renew & save
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="early-renewal-banner__crosses-second">
      <Crosses2 />
    </div>
    <EarlyRenewalModal
      v-if="showModal"
      :currentPlan="getCurrentSellablePlan"
      :promotionContent="currentPromotion.content"
      :paymentInformation="paymentInfo"
      :loading="loadingModal"
      @close-modal="showModal = false"
      @add-year-confirm="addYearConfirmation"
    />
    <div v-show="threeDSecure" id="recurly-3ds"></div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { format } from 'date-fns'
import Crosses1 from '@/assets/images/subscriptions/banners/crosses1.svg'
import Crosses2 from '@/assets/images/subscriptions/banners/crosses2.svg'
import redCrosses from '@/assets/images/subscriptions/banners/red-crosses.svg'
import EarlyRenewalModal from './EarlyRenewalModal.vue'

export default {
  name: 'EarlyRenewalBanner',
  components: {
    Crosses1: { name: 'Crosses1', ...Crosses1 },
    Crosses2: { name: 'Crosses2', ...Crosses2 },
    redCrosses: { name: 'redCrosses', ...redCrosses },
    EarlyRenewalModal,
  },
  props: {
    userCurrentPlan: {
      type: Object,
      required: true,
    },
    showBanner: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      prices: {
        fundamentals_annual: {
          crossed: '',
          valid: '',
        },
        ine_premium: {
          crossed: '',
          valid: '',
        },
      },
      currentPlanName: {
        fundamentals_annual: 'Fundamentals Annual',
        ine_premium: 'Premium',
      },
      showModal: false,
      loadingModal: false,
      threeDSecure: null,
    }
  },
  computed: {
    ...mapGetters('subscriptions', ['getCurrentSellablePlan']),
    ...mapState('subscriptions', ['promotionsAvailable', 'paymentInfo']),
    newExpirationDate() {
      if (this.showBanner) {
        const newDate = this.promotionsAvailable?.subscriptions_view_top_banner
          .content.new_renews_at
        return format(new Date(newDate), 'MMMM do, yyyy')
      }
      return ''
    },
    currentPlanCode() {
      return this.userCurrentPlan?.plan_code
    },
    currentPromotion() {
      return this.promotionsAvailable?.subscriptions_view_top_banner
    },
  },
  watch: {
    showBanner: {
      handler() {
        if (this.showBanner) {
          const content = this.promotionsAvailable
            ?.subscriptions_view_top_banner.content
          this.prices[
            this.currentPlanCode
          ].crossed = `${this.getCurrentSellablePlan?.price.substring(
            0,
            this.getCurrentSellablePlan?.price.length - 3
          )}`
          this.prices[this.currentPlanCode].valid = `$${content.price_usd}`
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('subscriptions', [
      'executePromotionBySlug',
      'changePromotionBannerVisibility',
      'getSubscriptions',
    ]),
    ...mapActions('app', [
      'setIsPageLoading',
      'setError',
      'setSuccess',
      'setNotification',
    ]),
    async addYearConfirmation(recurlyToken) {
      const recurlyTokenId = recurlyToken?.id
      this.loadingModal = true
      try {
        await this.executePromotionBySlug({
          slug: this.currentPromotion?.promo_slug,
          subscription_id: this.userCurrentPlan?.plan?.id,
        })
        this.loadingModal = false
        this.showModal = false
        this.setNotification(
          'Your renewal is being processed and should be complete momentarily. Please check back later. A new invoice will be sent to your email'
        )
        this.changePromotionBannerVisibility(false)
        this.$router.push({
          name: 'user-dashboard',
          params: { mode: 'subscriptions' },
        })
      } catch (error) {
        console.log(error)
        if (
          error?.response?.data?.processor_error_code ===
          'three_d_secure_action_required'
        ) {
          this.setNotification(error.response.data.error.message)
          this.start3dsFlow(error.response.data.actionTokenId, recurlyTokenId)
        } else if (error?.response?.data?.error?.message) {
          this.loadingModal = false
          this.showModal = false
          setTimeout(() => {
            this.setError(error.response.data.error.message)
          }, 1000)
        } else {
          this.setError(error)
        }
      }
      this.loadingModal = false
    },
    async threeDSecureResolved(recurlyTokenId, challengeToken) {
      try {
        await this.executePromotionBySlug({
          slug: this.currentPromotion?.promo_slug,
          subscription_id: this.userCurrentPlan?.plan?.id,
          payment_challenge_token: challengeToken,
          billing_token: challengeToken,
        })
      } catch (error) {
        this.setError(error.message)
      } finally {
        this.threeDSecure.remove()
      }
    },
    start3dsFlow(actionTokenId, recurlyTokenId) {
      const risk = window.recurly.Risk()
      this.threeDSecure = risk.ThreeDSecure({
        actionTokenId,
      })
      this.threeDSecure.on('token', token =>
        this.threeDSecureResolved(recurlyTokenId, token.id)
      )
      this.threeDSecure.on('error', error => {
        this.$store.dispatch(
          'app/setError',
          error && error.message
            ? error.message
            : 'We were unable to verify your order through 3D-Secure. Please contact your bank.'
        )
        this.threeDSecure.remove()
      })
      this.threeDSecure.attach(document.querySelector('#recurly-3ds'))
    },
  },
}
</script>
<style lang="scss" scoped>
.early-renewal-banner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  border-radius: 10px;
  background: linear-gradient(97deg, #5936e4 47.71%, #863ad2 93.87%);
  position: relative;
  overflow: hidden;

  &__container {
    display: flex;
    padding: 28px 21px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;
    border-radius: 10px;
    border: 1px solid var(--tuna-gray);
    position: relative;
  }
  &__content {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 20px;
    @include desktop {
      width: 691px;
    }
  }
  &__inner-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 30px;
    align-self: stretch;
    @include tablet {
      display: flex;
      align-items: center;
      gap: 30px;
      align-self: stretch;
      flex-direction: row;
    }
  }
  &__text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 23px;
    flex: 1 0 0;
  }
  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    align-self: stretch;
  }
  &__title {
    font-size: rem(20px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(26px);
    color: var(--white);
    @include tablet {
      font-size: 32px;
      line-height: 38px;
      align-self: stretch;
    }
  }
  &__subtitle {
    color: var(--white);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(24px);
    align-self: stretch;
    opacity: 0.7;
  }
  &__prices-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    align-self: stretch;
  }
  &__prices {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    align-self: stretch;
    color: var(--white);
    font-size: rem(32px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(38px);
    position: relative;
    &-crossed-svg {
      position: absolute;
      left: -3px;
      bottom: 14px;
      width: 73.275px;
      height: 20.627px;
      stroke-width: 4px;
      stroke: var(--carnation);
    }
  }
  &__small-print {
    color: var(--white);
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
    align-self: stretch;
    opacity: 0.7;
  }
  &__crosses-first {
    position: absolute;
    left: 554px;
    top: 20.5px;
    width: 157px;
    height: 204px;
    svg {
      overflow: visible;
    }
  }
  &__crosses-second {
    position: absolute;
    left: 705px;
    top: 141.313px;
    width: 41px;
    height: 41px;
    fill: #7356ec;
    svg {
      overflow: visible;
    }
  }
  &__cta {
    width: 100%;
    @include tablet {
      width: auto;
    }
    button {
      display: flex;
      padding: 8px 16px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      width: 100%;
      @include tablet {
        width: auto;
      }
    }
  }
}
</style>
